<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-flex>
        <dt-search-box @typing="searchValue" @reset-datatable="resetDataTable()" @clear-search-field="search = null" />
      </v-flex>
    </v-card>

    <v-card flat>
      <v-layout row>
        <v-layout row wrap justify-start align-end>
          <v-flex pa-2>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin:0 10px 0; height: 30px; width: 30px;"
                  :loading="loadThePrintBtn"
                  flat
                  fab
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 0; height: 30px; width: 30px"
                  fab
                  :loading="loadTheExportBtn"
                  flat
                  v-on="on"
                  color="primary"
                  @click="getNoPaginatedData('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row wrap justify-end class="pa-2 pr-3 pl-3">
          <v-btn
            :class="!$vuetify.rtl ? 'kt-addnew-button-en' : 'kt-addnew-button-ar'"
            dark
            color="primary"
            v-if="hasPermissions(['fixed_assets_store'])"
            @click="dialogs.addDialog = true"
          >
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
          <v-menu offset-y transition="slide-y-reverse-transition">
            <template v-slot:activator="{ on }">
              <a v-on="on" :class="!$vuetify.rtl ? 'import-dropdown-button-en' : 'import-dropdown-button-ar'">
                <v-icon color="white" style="padding-top: 7px">
                  keyboard_arrow_down
                </v-icon>
              </a>
            </template>
            <v-list>
              <v-list-tile @click="openImportComponent()">
                <v-icon>cloud_upload</v-icon>
                <v-list-tile-content class="px-2">
                  <v-list-tile-title>{{ $t('Import') }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.product_id }}</td>-->
          <td>{{ props.item.product_name }}</td>
          <td>{{ props.item.asset_type_name }}</td>
          <td>{{ props.item.product_description }}</td>

          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['fixed_assets_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.product_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Fixed Asset's Information`) }}</span>
                </v-tooltip>

                <v-tooltip v-if="hasPermissions(['fixed_assets_update'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="editRecord(props.item.product_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Fixed Asset's Information`) }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['fixed_assets_destroy'])" />

                <v-tooltip v-if="hasPermissions(['fixed_assets_destroy'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.product_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Fixed Asset Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-fixed-asset
      :addDialog="dialogs.addDialog"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
      @add-fixed-asset-type="dialogs.addFixedAssetType = true"
    />

    <edit-fixed-asset
      :editDialog="dialogs.editDialog"
      @updated="updateDatatableResult(), (dialogs.editDialog = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
      @add-fixed-asset-type="dialogs.addFixedAssetType = true"
    />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <view-fixed-asset :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <import-component
      :importDialog="dialogs.importDialog"
      :tableHeader="tableHeader"
      @hidemodal="dialogs.importDialog = false"
      :importUrl="'fixed_assets_import'"
      @imported="resetDataTable()"
    />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />

    <add-fixed-asset-type
      :addDialog="dialogs.addFixedAssetType"
      @reset-datatable="updateFullFixedAssetTypesList()"
      @hidemodal="dialogs.addFixedAssetType = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    importComponent: () => import('@/views/_partials/importComponent.vue'),
    addFixedAsset: () => import('./addFixedAsset.vue'),
    editFixedAsset: () => import('./editFixedAsset.vue'),
    viewFixedAsset: () => import('./viewFixedAsset.vue'),
    addFixedAssetType: () => import('@/views/inventory/fixedAssetTypes/addFixedAssetType.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        importDialog: false,
        printExportDialog: false,
        addFixedAssetType: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'product_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'product_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Name'), value: 'product_name' },
        { text: this.$t('Type'), value: 'asset_type_name' },
        { text: this.$t('Description'), value: 'product_description' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Fixed Assets'),
      icon: 'fa-dolly'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }

    this.updateFullFixedAssetTypesList()
  },

  computed: {
    ...mapGetters({
      resultData: 'fixedAssets/getFixedAssetsList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'fixedAssets/updateFixedAssetsList',
      deleteRecord: 'fixedAssets/deleteFixedAsset',
      viewInfo: 'fixedAssets/fetchFixedAssetInfo',
      updateFullFixedAssetTypesList: 'fixedAssetTypes/updateFullFixedAssetTypesList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    openImportComponent() {
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')
      this.dialogs.importDialog = true
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('fixed_assets_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['fixed_assets_show', 'fixed_assets_update', 'fixed_assets_destroy'])
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.viewInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.viewInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = { ...this.pagination, search: this.search }
          this.updateResult(payload)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    }
  }
}
</script>

<style scoped />
